import {from} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {
  dashboardGeneticsUploadStateDefaults,
  DashboardGeneticsUploadStateModel,
  dashboardGeneticsUploadUniqueStateId,
} from './dashboard-genetics-upload.models';
import {
  ClearUploadGeneticProgress,
  UploadGeneticFile,
  UploadGeneticFileFail,
  UploadGeneticFileSuccess,
} from './dashboard-genetics-upload.actions';
import {DashboardGeneticsUploadService} from '../../services/dashboard-genetics-upload/dashboard-genetics-upload.service';
import {BackendError} from '../../../messages/messages.actions';
import {computeFilesChecksumMd5} from '../../../shared/functions/files';
import {PROGRESS_STATUSES, UnknownError} from '../../../shared/symbols';


@State<DashboardGeneticsUploadStateModel>({
  name: dashboardGeneticsUploadUniqueStateId,
  defaults: dashboardGeneticsUploadStateDefaults,
})
@Injectable()
export class DashboardGeneticsUploadState {
  constructor(private geneticsUpload: DashboardGeneticsUploadService) {}

  @Selector()
  static geneticsUploadProgress(state: DashboardGeneticsUploadStateModel): PROGRESS_STATUSES {
    return state.uploadProgress;
  }

  @Selector()
  static geneticsUploadError(state: DashboardGeneticsUploadStateModel): UnknownError {
    return state.uploadBackendError;
  }

  @Action(UploadGeneticFile)
  uploadGeneticFile(ctx: StateContext<DashboardGeneticsUploadStateModel>, {genFile, patientUid}: UploadGeneticFile) {
    ctx.patchState({
      uploadProgress: PROGRESS_STATUSES.IN_PROGRESS,
      uploadBackendError: null,
    });

    from(computeFilesChecksumMd5([genFile]))
      .pipe(switchMap(checksum => this.geneticsUpload.uploadGenFile(genFile, checksum, patientUid)))
      .subscribe({
        next: () => ctx.dispatch(new UploadGeneticFileSuccess()),
        error: error => ctx.dispatch(new UploadGeneticFileFail(error)),
      });
  }

  @Action(UploadGeneticFileSuccess)
  uploadGeneticFileSuccess(ctx: StateContext<DashboardGeneticsUploadStateModel>) {
    ctx.patchState({
      uploadProgress: PROGRESS_STATUSES.SUCCEED,
    });
  }

  @Action(UploadGeneticFileFail)
  uploadGeneticFileFail(ctx: StateContext<DashboardGeneticsUploadStateModel>, {error}: UploadGeneticFileFail) {
    ctx.patchState({
      uploadProgress: PROGRESS_STATUSES.INTERRUPTED,
      uploadBackendError: error.error?.error || error.error?.file || error.message || error,
    });

    ctx.dispatch(new BackendError(error));
  }

  @Action(ClearUploadGeneticProgress)
  clearUploadGeneticProgress(ctx: StateContext<DashboardGeneticsUploadStateModel>) {
    ctx.patchState({
      uploadBackendError: null,
      uploadProgress: PROGRESS_STATUSES.NOT_INITIALIZED,
    });
  }
}
